import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faBaby,
  faWheelchairMove,
  faCheck,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faCircleXmark,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";
import {
  CalendarSvgIcon,
  PhoneSvgIcon,
  TableSvgIcon,
  UsersSvgIcon,
  UserSvgIcon,
  ClockSvgIcon,
  ZoneSvgIcon,
  NoPhoneSvgIcon,
  NoPhoneTwoLinesSvgIcon,
} from "../../icons/SvgIcons";
import {
  getReservationStatusAbreviationTranslation,
  getReservationStatusColors,
} from "../ReservationStatus";
import { DefaultDateUpper } from "../DefaultImports";
import Tooltip from "../ToolTipWrapper";

// Styled Components (compact)
const Card = styled.div`
  background: #fff;
  font-size: 15px;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column; /* rows stack vertically */
  gap: 3px;
  padding: 3px;
`;

const BoxRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BoxItems = styled.div`
  display: flex;
  align-items: center;
  margin: 2px 0px;
  color: #99b0ad;
`;

const ChangeHighlight = ({
  previous,
  current,
  isTablesUsed = false,
  isZones = false,
  isTime = false,
  isWaitingList = false,
}) => {
  const hasChanged =
    previous !== undefined && String(previous) !== String(current);
  const isPreviousUndefined =
    previous === undefined || previous === null || previous === "";
  const isCurrentUndefined =
    previous === undefined || current === null || current === "";

  const displayPrevious =
    isTablesUsed && isPreviousUndefined
      ? "Sem mesas"
      : isZones && isPreviousUndefined
      ? "Sala não definida"
      : previous;

  const displayCurrent =
    isTablesUsed && isCurrentUndefined
      ? "Sem mesas"
      : isZones && isPreviousUndefined
      ? "Qualquer sala"
      : isTime && isWaitingList
      ? "Hora registada " + current
      : current;

  return (
    <span
      style={{
        backgroundColor: hasChanged ? "#e6ffe6" : "transparent",
        padding: "1px 4px",
        borderRadius: "4px",
        color: hasChanged ? "green" : "#99b0ad",
        fontWeight: hasChanged ? "500" : "normal",
      }}
    >
      {hasChanged && <span style={{ color: "red" }}>{displayPrevious} → </span>}
      {displayCurrent}
    </span>
  );
};

const StatusHighlight = ({ previous, current }) => {
  const changed = previous !== undefined && previous !== current;
  const prevText = getReservationStatusAbreviationTranslation(previous);
  const currText = getReservationStatusAbreviationTranslation(current);
  const currColor = getReservationStatusColors(current);

  return (
    <>
      {changed && (
        <span style={{ color: "red", marginRight: "5px" }}>{prevText} →</span>
      )}
      <span
        style={{
          color: currColor,
          fontWeight: changed ? "500" : "normal",
        }}
      >
        {currText}
      </span>
    </>
  );
};

const ChangeDateHighlight = ({ previous, current }) => {
  const hasChanged =
    previous !== undefined && String(previous) !== String(current);

  const isPreviousEmpty =
    previous === undefined || previous === null || previous === "";
  const isCurrentEmpty =
    current === undefined || current === null || current === "";

  const displayPrevious = isPreviousEmpty ? "Data não definida" : previous;
  const displayCurrent = isCurrentEmpty ? "Data não definida" : current;

  return (
    <span
      style={{
        backgroundColor: hasChanged ? "#e6ffe6" : "transparent",
        padding: "1px 4px",
        borderRadius: "4px",
        color: hasChanged ? "green" : "#99b0ad",
        fontWeight: hasChanged ? "500" : "normal",
      }}
    >
      {hasChanged && (
        <span style={{ color: "red" }}>
          <DefaultDateUpper date={displayPrevious} /> →{" "}
        </span>
      )}
      <DefaultDateUpper date={displayCurrent} />
    </span>
  );
};

function ReservationItems({
  reservation,
  all_tables,
  previousReservationFromHistory,
  isReservationHistory = false,
  isReservationFromRestaurantScreen,
}) {
  const reservationZone = useSelector((state) => state.reservationZone);
  const { zones } = reservationZone;

  if (
    !reservation ||
    typeof reservation !== "object" ||
    Object.keys(reservation).length === 0
  )
    return null;

  const previousReservation = isReservationHistory
    ? previousReservationFromHistory
    : reservation.history[1];

  const getPrev = (key) => previousReservation?.[key];
  const getCurr = (key) =>
    key === "table_place_preference" && !isReservationHistory && reservation
      ? reservation.table_place_preference?.id
      : reservation?.[key];

  const parseTables = (tablesString) => {
    if (!tablesString || !all_tables) return [];
    return tablesString
      .split(",")
      .map((num) => parseInt(num, 10))
      .filter((num) => !Number.isNaN(num))
      .sort((a, b) => a - b)
      .map((tableId) => {
        const tableObj = all_tables.find((t) => t.id === tableId);
        return tableObj?.table_name || `Mesa ${tableId}`;
      });
  };

  const getPhoneConfirmationText = (value, phoneNumber) => {
    if (
      !phoneNumber ||
      phoneNumber.trim() === "" ||
      phoneNumber.trim().length <= 3
    ) {
      return (
        <Tooltip text="Sem número de telefone">
          <NoPhoneTwoLinesSvgIcon
            width={18}
            height={16}
            strokeColor="#99B0AD"
          />
        </Tooltip>
      );
    }

    if (value === true) {
      return (
        <Tooltip text="Telefone confirmado">
          <PhoneSvgIcon width={18} height={16} strokeColor="#99B0AD" />
        </Tooltip>
      );
    }

    return (
      <Tooltip text="Telefone não confirmado">
        <NoPhoneSvgIcon width={18} height={16} strokeColor="#99B0AD" />
      </Tooltip>
    );
  };

  function formatPhoneNumber(phone, countryCode) {
    if (!phone || !countryCode) return phone;

    const cleanCode = countryCode.replace(/\D/g, ""); // remove any non-numeric
    const codeLength = cleanCode.length;

    // Ensure the phone starts with the country code (optional logic safeguard)
    if (phone.startsWith(cleanCode)) {
      return `+${cleanCode} ${phone.slice(codeLength)}`;
    }

    // Fallback: insert space after the expected number of digits
    return `+${cleanCode} ${phone}`;
  }

  return (
    <Card>
      <Box>
        {/* Nome */}
        {isReservationHistory && (
          <BoxItems>
            <UserSvgIcon
              width={20}
              height={18}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              previous={getPrev("full_name")}
              current={getCurr("full_name")}
            />
          </BoxItems>
        )}
        {/* Telemóvel e Confirmação */}
        <BoxRow>
          {isReservationHistory && (
            <BoxItems>
              <PhoneSvgIcon
                width={20}
                height={18}
                strokeColor="#99B0AD"
                style={{ marginRight: "4px" }}
              />
              <ChangeHighlight
                previous={
                  <>
                    {formatPhoneNumber(
                      getPrev("phone_number"),
                      previousReservationFromHistory?.country_code
                    )}{" "}
                    {getPhoneConfirmationText(
                      getPrev("is_phone_confirmed"),
                      getPrev("phone_number")
                    )}
                  </>
                }
                current={
                  <>
                    {formatPhoneNumber(
                      getCurr("phone_number"),
                      reservation?.country_code
                    )}{" "}
                    {getPhoneConfirmationText(
                      getCurr("is_phone_confirmed"),
                      getCurr("phone_number")
                    )}
                  </>
                }
              />
            </BoxItems>
          )}
          {/* Data */}
          {!isReservationFromRestaurantScreen && (
            <BoxItems>
              <CalendarSvgIcon
                width={20}
                height={18}
                strokeColor="#99B0AD"
                style={{ marginRight: "4px" }}
              />
              <ChangeDateHighlight
                previous={getPrev("date")}
                current={getCurr("date")}
              />
            </BoxItems>
          )}
        </BoxRow>
        <BoxRow>
          {/* Hora */}
          <BoxItems>
            <ClockSvgIcon
              width={22}
              height={20}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              isTime={true}
              isWaitingList={reservation.waiting_list_number != null}
              previous={getPrev("time")}
              current={getCurr("time")}
            />
          </BoxItems>
          {/* Nº de Pessoas */}
          <BoxItems>
            <UsersSvgIcon
              width={24}
              height={22}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              previous={getPrev("number_of_people")}
              current={getCurr("number_of_people")}
            />
          </BoxItems>
          {/* Cadeira de bebé */}
          {reservation.baby_chair !== 0 && (
            <BoxItems>
              <FontAwesomeIcon
                icon={faBaby}
                size="xl"
                style={{ color: "#99B0AD", marginRight: "4px" }}
              />
              <ChangeHighlight
                previous={getPrev("baby_chair")}
                current={getCurr("baby_chair")}
              />
            </BoxItems>
          )}
        </BoxRow>

        <BoxRow>
          {/* Zona */}
          <BoxItems>
            <ZoneSvgIcon
              width={22}
              height={20}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              previous={
                zones?.zones_with_id?.[getPrev("table_place_preference")]
              }
              current={
                zones?.zones_with_id?.[getCurr("table_place_preference")]
              }
              isZones={true}
            />
          </BoxItems>
        </BoxRow>
        <BoxRow>
          {/* Mesas usadas */}
          {(getCurr("tablesused") || getPrev("tablesused")) &&
            all_tables &&
            reservation.status !== "Cancelled" && (
              <BoxItems>
                <TableSvgIcon
                  width={24}
                  height={22}
                  strokeColor="#99B0AD"
                  style={{ marginRight: "4px" }}
                />
                <ChangeHighlight
                  previous={parseTables(getPrev("tablesused")).join(", ")}
                  current={parseTables(getCurr("tablesused")).join(", ")}
                  isTablesUsed={true}
                />
              </BoxItems>
            )}
          {/* Erro: mesas insuficientes */}
          {reservation.not_enough_seats &&
            reservation.waiting_list_number === null && (
              <BoxItems
                style={{
                  backgroundColor: "#ffe6e6",
                  borderRadius: "4px",
                  padding: "4px 6px",
                }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  style={{
                    color: "#d15823",
                    fontSize: ".8rem",
                    marginRight: "6px",
                  }}
                />
                <span style={{ color: "#d15823", fontSize: ".8rem" }}>
                  Mesas insuficientes
                </span>
              </BoxItems>
            )}
        </BoxRow>
        <BoxRow></BoxRow>

        {/* Estado */}
        {isReservationHistory && (
          <BoxItems>
            <FontAwesomeIcon
              icon={faCheck}
              size="xl"
              style={{ color: "#99B0AD", marginRight: "4px" }}
            />
            <StatusHighlight
              previous={getPrev("status")}
              current={getCurr("status")}
            />
          </BoxItems>
        )}
      </Box>
      <BoxRow>
        {/* Acessibilidade */}
        {reservation.accessability === "wheel_chair" && (
          <BoxItems>
            <FontAwesomeIcon
              icon={faWheelchairMove}
              size="xl"
              style={{ color: "#99B0AD", marginLeft: "4px" }}
            />
            <ChangeHighlight
              previous={
                getPrev("accessability") === "wheel_chair"
                  ? "Mob. Red."
                  : "Nenhuma"
              }
              current={
                getCurr("accessability") === "wheel_chair"
                  ? "Mob. Red."
                  : "Nenhuma"
              }
            />
          </BoxItems>
        )}
      </BoxRow>
    </Card>
  );
}

export default ReservationItems;
